import { CreateIncidentDTO, Incident } from '../types/incident';
import { API_BASE, getApiHeaders } from './apiClient';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: { ...getApiHeaders(), ...options?.headers },
      ...options,
    });
  } catch {
    throw new Error('Cannot reach the CIMA backend. Make sure it is running on port 3004.');
  }

  const text = await res.text();
  let data: any;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Server returned an unexpected response (status ${res.status}). Check that the backend is running.`);
  }

  if (!res.ok) {
    // Surface the TEAMS_REQUIRED error to the global modal so the UX
    // is consistent regardless of which entry point hit the gate.
    if (data?.errorCode === 'TEAMS_REQUIRED') {
      window.dispatchEvent(new CustomEvent('cima:api-error', { detail: { code: 'TEAMS_REQUIRED', error: data.error } }));
    }
    const err = new Error(data.error || `Request failed: ${res.status}`);
    (err as any).code = data?.errorCode;
    throw err;
  }
  return data as T;
}

export async function createIncident(dto: CreateIncidentDTO): Promise<Incident> {
  const data = await apiFetch<{ incident: Incident }>('/cima/incidents', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
  return data.incident;
}

export async function listIncidents(): Promise<Incident[]> {
  const data = await apiFetch<{ incidents: Incident[] }>('/cima/incidents');
  return data.incidents;
}

export async function getIncident(id: string): Promise<Incident> {
  const data = await apiFetch<{ incident: Incident }>(`/cima/incidents/${id}`);
  return data.incident;
}

export async function updateIncidentStatus(
  id: string,
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'REOPENED',
): Promise<Incident> {
  const data = await apiFetch<{ incident: Incident }>(`/cima/incidents/${id}`, {
    method: 'PATCH',
    body:   JSON.stringify({ status }),
  });
  return data.incident;
}

export interface UpdateIncidentDTO {
  title?:            string;
  type?:             string;
  description?:      string;
  location?:         string;
  occurredAt?:       string;
  severity?:         string;
  peopleInvolved?:   string;
  immediateActions?: string;
}

export async function updateIncident(id: string, dto: UpdateIncidentDTO): Promise<Incident> {
  const data = await apiFetch<{ incident: Incident }>(`/cima/incidents/${id}`, {
    method: 'PUT',
    body:   JSON.stringify(dto),
  });
  return data.incident;
}

/** Merge `sourceIncidentId` into `primaryId`. Source ends up MERGED with a
 *  pointer back; chat history + sitrep narrative are folded into primary. */
export async function mergeIncidents(primaryId: string, sourceIncidentId: string): Promise<{
  merged: boolean; primaryId: string; sourceIncidentId: string;
}> {
  return apiFetch(`/cima/incidents/${primaryId}/merge`, {
    method: 'POST',
    body:   JSON.stringify({ sourceIncidentId }),
  });
}
